import styled from "@emotion/styled";
import { SourceObservable } from "@/store/sources/SourceObservable";
import { observer } from "mobx-react-lite";
import {
  MDSChip,
  MDSChipVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MDSFloatingOverlaySlotName } from "@/mds/components/overlays/floating-overlay/floating-overlay.types";
import { MDSFloatingOverlay } from "@/mds/components/overlays/floating-overlay/floating-overlay";
import { MutableRefObject, useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { FloatingEmailPreviewContent } from "@/components/note/editor/source-bar/floating-email-preview";
import { FloatingTextMessagePreviewContent } from "@/components/note/editor/source-bar/floating-text-message-preview";
import { INoteObservable } from "@/store/note";
import { useMouseOverOut } from "@/domains/react/useMouseOverOut";
import { FloatingApiRequestPreviewContent } from "@/components/note/editor/source-bar/floating-api-request-preview";
import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import {
  NOTE_SOURCE_CHIP_ICON_MAP,
  NOTE_SOURCE_LABEL_MAP,
} from "@/components/note/editor/source-bar/note-editor-source-bar-chip.constants";
import { FloatingWebSitePreviewContent } from "@/components/note/editor/source-bar/floating-website-preview";
import { useNoteSourceArtifactInfo } from "@/components/note/editor/source-bar/utils/use-note-source-artifact-info";
import { useNoteSourceExtendedArtifactInfo } from "@/components/note/editor/source-bar/utils/use-note-source-extended-artifact-info";
import { truncate } from "lodash-es";
import {
  AudioSourceAvailabilityState,
  FloatingAudioPreviewContent,
  FloatingAudioPreviewDropdownMenu,
  getAudioSourceAvailability,
  resolveAudioSourceLabel,
  resolveAudioSourceSubtitle,
  useAudioRecordingDownloadUrl,
  useAudioRecordingNoteSourceArtifactInfo,
  useRetranscribeVoiceSource,
} from "@/components/note/editor/source-bar/voice";
import { ZIndex } from "@/domains/design/constants";
import { FloatingCalendarEventPreview } from "@/components/note/editor/source-bar/floating-calendar-event-preview";
import { getMeetingPlatformFromUrl } from "@/domains/calendar/getMeetingPlatformFromUrl";
import { useAppStore } from "@/store";

const MAX_CHIP_LABEL_LENGTH = 36;

interface NoteEditorSourceBarChipProps {
  source: SourceObservable;
  note: INoteObservable;
  playingSourceIdRef: MutableRefObject<string | null>;
  isCollapsed: boolean;
}

export const NoteEditorSourceBarChip = observer<NoteEditorSourceBarChipProps>(
  ({ source, note, playingSourceIdRef, isCollapsed }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);

    const updateIsOpen = useCallback(
      (isOpen: boolean, config?: { force?: boolean }) => {
        if (config?.force) {
          setIsOpen(isOpen);
          return;
        }

        if (playingSourceIdRef.current) return;
        if (isDropdownMenuOpen) return;

        setIsOpen(isOpen);
      },
      [playingSourceIdRef, isDropdownMenuOpen]
    );

    const { handleMouseEnter, handleMouseLeave } = useMouseOverOut({
      isDropdownMenuOpen,
      setIsOpen: updateIsOpen,
    });

    const { store } = useAppStore();
    const audioAvailability = getAudioSourceAvailability({ store, source });

    // For audio sources, we avoid remote fetch until the recording is ready and fall back
    // to locally cached transcript turns while uploading.
    const shouldFetchArtifact = isOpen && source.sourceKind !== "AUDIO";

    const artifactInfo = useNoteSourceArtifactInfo({
      sourceId: source.id,
      isOpen: shouldFetchArtifact,
      sourceKind: source.sourceKind,
    });

    const audioArtifactInfo = useAudioRecordingNoteSourceArtifactInfo({
      sourceId: source.id,
      isOpen,
      isEnabled: isOpen && source.sourceKind === "AUDIO",
      audioRecordingId: audioAvailability.audioRecordingId,
      availabilityState: audioAvailability.state,
    });

    const resolvedArtifactInfo = source.sourceKind === "AUDIO" ? audioArtifactInfo : artifactInfo;

    const shouldPrepareDownload =
      isOpen && audioAvailability.state === AudioSourceAvailabilityState.Ready;
    const {
      downloadUrl: preparedDownloadUrl,
      isPreparing: isPreparingDownload,
      error: downloadPrepareError,
    } = useAudioRecordingDownloadUrl({
      audioRecordingId: audioAvailability.audioRecordingId,
      isEnabled: shouldPrepareDownload,
    });

    /**
     * This is a lightweight implementation for text message sources.
     * In the future, we have plans to roll out a more comprehensive solution
     * for text messages (where we can scroll through the full history).
     */
    const extendedArtifactInfo = useNoteSourceExtendedArtifactInfo({
      noteId: note.id,
      isOpen: true,
      sourceKind: source.sourceKind,
    });

    const truncatedLabel = useMemo(() => {
      if (isCollapsed) return null;
      return truncate(source.primaryLabel, {
        length: MAX_CHIP_LABEL_LENGTH,
        omission: "...",
      });
    }, [source.primaryLabel, isCollapsed]);

    const closeOverlay = useCallback(() => {
      setIsDropdownMenuOpen(false);
      updateIsOpen(false, { force: true });
    }, [updateIsOpen, setIsDropdownMenuOpen]);

    const { isRetranscribing, handleRetranscribe } = useRetranscribeVoiceSource({
      artifactInfo: resolvedArtifactInfo,
      sourceId: source.id,
      downloadUrl: preparedDownloadUrl,
      audioRecordingId: audioAvailability.audioRecordingId,
    });

    const audioArtifactData =
      resolvedArtifactInfo?.kind === "AUDIO" ? resolvedArtifactInfo.data : null;

    return (
      <Wrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <MDSFloatingOverlay
          isOpen={isOpen}
          onOpenChange={updateIsOpen}
          popoverProps={{
            placement: "top",
            isNonModal: true, // Makes it work better with hover behavior
            zIndex: ZIndex.SourcesPreview,
          }}
        >
          <MDSChip
            variant={MDSChipVariant.Small}
            slot={MDSFloatingOverlaySlotName.Trigger}
            icon={NOTE_SOURCE_CHIP_ICON_MAP[source.sourceKind]}
            onPress={() => updateIsOpen(!isOpen, { force: true })}
          >
            {truncatedLabel}
          </MDSChip>
          <HeaderLeft slot={MDSFloatingOverlaySlotName.HeaderLeft}>
            <Heading>
              <MDSText
                variant={MDSTextVariant.TextS}
                weight={MDSTextWeight.Smedium}
                color={MDSTextColor.Contrast}
              >
                {getSourceLabel({
                  artifactInfo: resolvedArtifactInfo,
                  audioAvailability: audioAvailability.state,
                })}
              </MDSText>
              <MDSText variant={MDSTextVariant.TextXs}>
                {getSubtitle({
                  artifactInfo: resolvedArtifactInfo,
                  audioAvailability: audioAvailability.state,
                })}
              </MDSText>
            </Heading>
          </HeaderLeft>
          <div slot={MDSFloatingOverlaySlotName.HeaderRight}>
            <StyledTopRightButtonWrapper>
              {source.sourceKind === "AUDIO" &&
                audioAvailability.state !== AudioSourceAvailabilityState.Recording && (
                  <FloatingAudioPreviewDropdownMenu
                    noteId={note.id}
                    sourceId={source.id}
                    artifactInfo={resolvedArtifactInfo}
                    availabilityState={audioAvailability.state}
                    audioRecordingId={audioAvailability.audioRecordingId}
                    downloadUrl={preparedDownloadUrl}
                    isPreparingDownload={isPreparingDownload}
                    isRetranscribing={isRetranscribing}
                    handleRetranscribe={handleRetranscribe}
                    isDropdownMenuOpen={isDropdownMenuOpen}
                    setIsDropdownMenuOpen={setIsDropdownMenuOpen}
                  />
                )}
              <MDSIconButton slot="close" iconKind={MDSIconKind.Exit} onClick={closeOverlay} />
            </StyledTopRightButtonWrapper>
          </div>
          <div slot={MDSFloatingOverlaySlotName.Content}>
            {resolvedArtifactInfo?.kind === "EMAIL" && (
              <FloatingEmailPreviewContent
                isLoading={resolvedArtifactInfo.isLoading}
                artifactData={resolvedArtifactInfo.data}
                isOpen={isOpen}
              />
            )}
            {extendedArtifactInfo?.kind === "TEXT_MESSAGE" && (
              <FloatingTextMessagePreviewContent
                isLoading={resolvedArtifactInfo?.isLoading || extendedArtifactInfo.isLoading}
                isOpen={isOpen}
                extendedArtifactInfo={extendedArtifactInfo}
              />
            )}
            {resolvedArtifactInfo?.kind === "API_REQUEST" && (
              <FloatingApiRequestPreviewContent
                isLoading={resolvedArtifactInfo.isLoading}
                artifactData={resolvedArtifactInfo.data}
              />
            )}
            {resolvedArtifactInfo?.kind === "API_REQUEST_VIA_ZAPIER" && (
              <FloatingApiRequestPreviewContent
                isLoading={resolvedArtifactInfo.isLoading}
                artifactData={resolvedArtifactInfo.data}
              />
            )}
            {source.sourceKind === "AUDIO" && (
              <FloatingAudioPreviewContent
                isLoading={resolvedArtifactInfo?.isLoading ?? false}
                isOpen={isOpen}
                artifactData={audioArtifactData}
                playingSourceIdRef={playingSourceIdRef}
                sourceId={source.id}
                noteId={note.id}
                noteTitle={note.title}
                isRetranscribing={isRetranscribing}
                availabilityState={audioAvailability.state}
                audioRecordingId={audioAvailability.audioRecordingId}
                downloadUrl={preparedDownloadUrl}
                isPreparingDownload={isPreparingDownload}
                downloadPrepareError={downloadPrepareError}
              />
            )}
            {resolvedArtifactInfo?.kind === "WEB_CLIP" && (
              <FloatingWebSitePreviewContent
                isLoading={resolvedArtifactInfo.isLoading}
                artifactData={resolvedArtifactInfo.data}
                isOpen={isOpen}
              />
            )}
            {resolvedArtifactInfo?.kind === "CALENDAR_EVENT" && source.associatedArtifactId && (
              <FloatingCalendarEventPreview
                isLoading={resolvedArtifactInfo.isLoading}
                artifactData={resolvedArtifactInfo.data}
                isOpen={isOpen}
                connectedCalendarEventId={source.associatedArtifactId}
              />
            )}
          </div>
        </MDSFloatingOverlay>
      </Wrapper>
    );
  }
);

const getSourceLabel = ({
  artifactInfo,
  audioAvailability,
}: {
  artifactInfo: NoteSourceArtifactInfo | null;
  audioAvailability?: AudioSourceAvailabilityState;
}) => {
  if (!artifactInfo) {
    return "";
  }

  if (artifactInfo.kind === "AUDIO") {
    return resolveAudioSourceLabel({
      availabilityState: audioAvailability,
    });
  }

  if (artifactInfo.kind === "WEB_CLIP") {
    return artifactInfo.data?.website_title;
  }

  if (artifactInfo.kind === "CALENDAR_EVENT") {
    return (
      <StyledCalendarEventTitleWrapper>
        {artifactInfo.data?.event_title}
        {artifactInfo.data?.calendar_name && (
          <StyledCalendarEventTitleCalendarLabel
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Regular}
            color={MDSTextColor.Secondary}
          >
            <MDSIcon
              kind={MDSIconKind.Dot}
              size={10}
              overrideColor={artifactInfo.data?.calendar_background_color ?? undefined}
            />
            {artifactInfo.data?.calendar_name}
          </StyledCalendarEventTitleCalendarLabel>
        )}
      </StyledCalendarEventTitleWrapper>
    );
  }

  if (artifactInfo?.kind !== "EMAIL") {
    return NOTE_SOURCE_LABEL_MAP[artifactInfo?.kind];
  }

  if (artifactInfo?.data?.sender_name) {
    return `${NOTE_SOURCE_LABEL_MAP[artifactInfo.kind]} ${artifactInfo.data?.sender_name}`;
  }

  return `${NOTE_SOURCE_LABEL_MAP[artifactInfo.kind]} ...`;
};

const getSubtitle = ({
  artifactInfo,
  audioAvailability,
}: {
  artifactInfo: NoteSourceArtifactInfo | null;
  audioAvailability?: AudioSourceAvailabilityState;
}) => {
  if (!artifactInfo) {
    return "";
  }

  if (artifactInfo.kind === "AUDIO") {
    return resolveAudioSourceSubtitle({
      availabilityState: audioAvailability,
    });
  }

  if (artifactInfo.kind === "EMAIL") {
    if (artifactInfo.data?.sent_at) {
      return DateTime.fromISO(artifactInfo.data.sent_at).toFormat("MMMM d, yyyy");
    }
  }

  // TODO: Expand when we have text message data
  if (artifactInfo.kind === "TEXT_MESSAGE") {
    if (artifactInfo.data?.received_at) {
      return DateTime.fromISO(artifactInfo.data.received_at).toFormat("MMMM d, yyyy");
    }
  }

  if (artifactInfo.kind === "API_REQUEST") {
    if (artifactInfo.data?.processed_at) {
      return DateTime.fromISO(artifactInfo.data.processed_at).toFormat("MMMM d, yyyy");
    }
  }

  if (artifactInfo.kind === "API_REQUEST_VIA_ZAPIER") {
    if (artifactInfo.data?.processed_at) {
      return DateTime.fromISO(artifactInfo.data.processed_at).toFormat("MMMM d, yyyy");
    }
  }

  if (artifactInfo.kind === "WEB_CLIP") {
    return artifactInfo.data?.website_url;
  }

  if (artifactInfo.kind === "CALENDAR_EVENT") {
    return getMeetingPlatformFromUrl(artifactInfo.data?.meet_link);
  }

  return "";
};

const Wrapper = styled.div``;

const HeaderLeft = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.spacing["1"]};
`
);

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  text-overflow: ellipsis;
  max-width: min(500px, 90vw) !important;
`;

const StyledTopRightButtonWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${theme.spacing["1"]};
`
);

const StyledCalendarEventTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const StyledCalendarEventTitleCalendarLabel = styled(MDSText)`
  display: flex;
  align-items: center;
`;
